import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { updateListingSearchKeywords as apiUpdateListingSearchKeywords } from '../util/api';

// ── Types ──────────────────────────────────────────────────────────────────

interface UpdateResult {
  success: boolean;
  /** Names of the publicData keys that were written, e.g. ['locationKeywords']. */
  updated: string[];
}

interface ListingSearchKeywordsState {
  updateInProgress: boolean;
  updateError: string | null;
}

// ── Thunk ──────────────────────────────────────────────────────────────────

/**
 * Fire-and-forget thunk that tells the server to recompute `locationKeywords`
 * and `dimensionKeywords` for a listing the current user owns.
 *
 * The server fetches the listing via the user's session, derives the indexed
 * text fields from `publicData`, and writes them back via `ownListings.update`,
 * no integration SDK involved.
 *
 * @param listingId - UUID string of the own listing to update.
 */
export const updateListingSearchKeywords = createAsyncThunk<UpdateResult, string>(
  'listingSearchKeywords/update',
  async (listingId, { rejectWithValue }) => {
    try {
      return await apiUpdateListingSearchKeywords(listingId) as UpdateResult;
    } catch (e: any) {
      return rejectWithValue(e.message as string);
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────────

const slice = createSlice({
  name: 'listingSearchKeywords',
  initialState: {
    updateInProgress: false,
    updateError: null,
  } as ListingSearchKeywordsState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(updateListingSearchKeywords.pending, state => {
        state.updateInProgress = true;
        state.updateError = null;
      })
      .addCase(updateListingSearchKeywords.fulfilled, state => {
        state.updateInProgress = false;
      })
      .addCase(updateListingSearchKeywords.rejected, (state, action) => {
        state.updateInProgress = false;
        state.updateError = action.payload as string;
      });
  },
});

export default slice.reducer;
